import React from 'react'
import { notification } from 'antd'
import './PanelImages.css'

const PanelImages = ({ images, setImages }) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false)
    const [currentIndex, setCurrentIndex] = React.useState(0)
    const fileInputRef = React.useRef(null)

    const visibleImages = images.filter(img => !img.markedForDeletion)

    const openModal = index => {
        setCurrentIndex(index)
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setCurrentIndex(0)
    }

    const navigate = direction => {
        setCurrentIndex(prevIndex => {
            const total = visibleImages.length
            if (total === 0) return prevIndex
            return (prevIndex + direction + total) % total
        })
    }

    React.useEffect(() => {
        const handleKeyDown = e => {
            if (!isModalOpen) return
            if (e.key === 'ArrowLeft') navigate(-1)
            if (e.key === 'ArrowRight') navigate(1)
            if (e.key === 'Escape') closeModal()
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, visibleImages.length])

    React.useEffect(() => {
        if (currentIndex >= visibleImages.length && visibleImages.length > 0) {
            setCurrentIndex(visibleImages.length - 1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleImages.length])

    React.useEffect(() => {
        return () => {
            images.forEach(img => {
                if (img.originFileObj && img.url) {
                    URL.revokeObjectURL(img.url)
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDropzoneClick = () => {
        if (fileInputRef.current) fileInputRef.current.click()
    }

    const handleFileChange = e => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        const newImages = files.map(file => {
            const objectUrl = URL.createObjectURL(file)
            return {
                uid: `new-${Date.now()}-${Math.random()}`,
                url: objectUrl,
                fullUrl: objectUrl,
                isMain: false,
                originFileObj: file,
            }
        })

        const hasExistingMain = visibleImages.some(img => img.isMain)
        if (!hasExistingMain && newImages.length > 0) {
            newImages[0].isMain = true
        }

        setImages(images.concat(newImages))
        e.target.value = ''
    }

    const currentImage = visibleImages[currentIndex] || null

    const handleSetMain = () => {
        if (!currentImage || currentImage.isMain) return

        setImages(
            images.map(img => ({
                ...img,
                isMain: img.uid === currentImage.uid,
            }))
        )
        notification.info({
            message: 'Se definirá como principal al guardar',
        })
    }

    const handleDelete = () => {
        if (!currentImage) return

        const wasMain = currentImage.isMain
        const remainingVisible = visibleImages.filter(img => img.uid !== currentImage.uid)
        const nextMainUid = wasMain && remainingVisible[0] ? remainingVisible[0].uid : null

        if (!Number.isInteger(currentImage.uid)) {
            if (currentImage.originFileObj && currentImage.url) {
                URL.revokeObjectURL(currentImage.url)
            }
            setImages(
                images
                    .filter(img => img.uid !== currentImage.uid)
                    .map(img => (nextMainUid && img.uid === nextMainUid ? { ...img, isMain: true } : img))
            )
            return
        }

        setImages(
            images.map(img => {
                if (img.uid === currentImage.uid) {
                    return { ...img, markedForDeletion: true, isMain: false }
                }
                if (nextMainUid && img.uid === nextMainUid) {
                    return { ...img, isMain: true }
                }
                return img
            })
        )
        notification.info({
            message: 'Se eliminará al guardar los cambios',
        })
    }

    return (
        <div className="media-uploader-card">
            <div className="media-dropzone" onClick={handleDropzoneClick}>
                <input
                    type="file"
                    multiple
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <p>
                    <span className="media-btn-link">Agregar archivos</span> o haz click para subir imágenes
                    {visibleImages.length > 0 ? ` (${visibleImages.length})` : ''}
                </p>
            </div>

            <div className="media-carousel-track">
                {visibleImages.map((img, idx) => (
                    <div
                        key={img.uid}
                        className={`media-thumb-card ${img.isMain ? 'is-main' : ''}`}
                        onClick={() => openModal(idx)}
                    >
                        {img.isMain && <span className="media-main-badge">Principal</span>}
                        <img src={img.url} alt={`Imagen ${idx + 1}`} />
                    </div>
                ))}
            </div>

            {isModalOpen && currentImage && (
                <div className="media-modal-backdrop" onClick={closeModal}>
                    <div className="media-modal-box" onClick={e => e.stopPropagation()}>
                        <div className="media-modal-header">
                            <span className="media-modal-counter">
                                {currentIndex + 1} de {visibleImages.length}
                            </span>
                            <button className="media-btn-close" onClick={closeModal}>
                                &times;
                            </button>
                        </div>

                        <div className="media-modal-body">
                            {visibleImages.length > 1 && (
                                <button
                                    className="media-nav-arrow media-nav-prev"
                                    onClick={() => navigate(-1)}
                                >
                                    &#10094;
                                </button>
                            )}

                            <img src={currentImage.fullUrl} alt={`Vista previa ${currentIndex + 1}`} />

                            {visibleImages.length > 1 && (
                                <button
                                    className="media-nav-arrow media-nav-next"
                                    onClick={() => navigate(1)}
                                >
                                    &#10095;
                                </button>
                            )}
                        </div>

                        <div className="media-modal-footer">
                            <span className="media-status-badge">
                                {currentImage.isMain ? '● Imagen principal' : ''}
                            </span>

                            <div className="media-actions-group">
                                <button
                                    className="media-btn media-btn-secondary"
                                    onClick={handleSetMain}
                                    disabled={currentImage.isMain}
                                >
                                    {currentImage.isMain ? 'Es la principal' : 'Definir como principal'}
                                </button>
                                <button
                                    className="media-btn media-btn-danger"
                                    onClick={handleDelete}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PanelImages
